import { PropertyStructure } from "@/types/types";
import { Building2, Home } from "lucide-react";

interface PropertyTypeSelectorProps {
  value?: PropertyStructure;
  onChange: (value: PropertyStructure) => void;
}

export default function PropertyTypeSelector({
  value,
  onChange,
}: PropertyTypeSelectorProps) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-semibold text-foreground">
          What type of property are you adding?
        </h2>
        <p className="mt-3 text-base text-muted-foreground">
          Choose between a single unit or a multi-unit building
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
        {/* Single Unit Option */}
        <button
          type="button"
          onClick={() => onChange("single-unit")}
          className={`group relative p-8 rounded-2xl border-2 transition-all duration-200 text-left ${
            value === "single-unit"
              ? "border-[#007AFF] shadow-lg"
              : "border-border hover:border-muted-foreground/50 hover:shadow-md"
          }`}
        >
          <div
            className={`absolute top-4 right-4 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
              value === "single-unit"
                ? "border-[#007AFF] bg-[#007AFF]"
                : "border-border"
            }`}
          >
            {value === "single-unit" && (
              <div className="w-3 h-3 bg-white rounded-full" />
            )}
          </div>

          <div
            className={`w-16 h-16 rounded-full flex items-center justify-center mb-6 ${
              value === "single-unit"
                ? "bg-[#007AFF] text-white"
                : "bg-muted text-muted-foreground group-hover:bg-muted/80"
            }`}
          >
            <Home className="h-8 w-8" />
          </div>

          <h3 className="text-2xl font-semibold text-foreground mb-3">
            Single Unit
          </h3>
          <p className="text-muted-foreground leading-relaxed">
            One complete property unit like a house, duplex, bungalow, or
            detached home. Perfect for standalone properties.
          </p>

          <div className="mt-6 space-y-2">
            <div className="flex items-center text-sm text-neutral-600">
              One tenant per property
            </div>
            <div className="flex items-center text-sm text-neutral-600">
              Simple setup process
            </div>
            <div className="flex items-center text-sm text-neutral-600">
              Quick listing creation
            </div>
          </div>
        </button>

        {/* Multi-Unit Option */}
        <button
          type="button"
          onClick={() => onChange("multi-unit")}
          className={`group relative p-8 rounded-2xl border-2 transition-all duration-200 text-left ${
            value === "multi-unit"
              ? "border-[#007AFF] shadow-lg"
              : "border-border hover:border-muted-foreground/50 hover:shadow-md"
          }`}
        >
          <div
            className={`absolute top-4 right-4 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
              value === "multi-unit"
                ? "border-[#007AFF] bg-[#007AFF]"
                : "border-border"
            }`}
          >
            {value === "multi-unit" && (
              <div className="w-3 h-3 bg-white rounded-full" />
            )}
          </div>

          <div
            className={`w-16 h-16 rounded-full flex items-center justify-center mb-6 ${
              value === "multi-unit"
                ? "bg-[#007AFF] text-white"
                : "bg-muted text-muted-foreground group-hover:bg-muted/80"
            }`}
          >
            <Building2 className="h-8 w-8" />
          </div>

          <h3 className="text-2xl font-semibold text-foreground mb-3">
            Multi-Unit Building
          </h3>
          <p className="text-muted-foreground leading-relaxed">
            An apartment block or complex with multiple units. Manage several
            flats or apartments within one property.
          </p>

          <div className="mt-6 space-y-2">
            <div className="flex items-center text-sm text-gray-600">
              Multiple tenants per building
            </div>
            <div className="flex items-center text-sm text-gray-600">
              Individual unit management
            </div>
            <div className="flex items-center text-sm text-gray-600">
              Bulk unit creation available
            </div>
          </div>
        </button>
      </div>

      {value && (
        <div className="mt-8 p-4 border border-blue-200 dark:border-blue-900 rounded-xl">
          <p className="text-sm text-blue-700">
            ✓ Great choice! You selected{" "}
            <span className="font-semibold">
              {value === "single-unit" ? "Single Unit" : "Multi-Unit Building"}
            </span>
            . Click "Next" to continue.
          </p>
        </div>
      )}
    </div>
  );
}
